import * as Locales from 'date-fns/locale';
import { Locale } from 'date-fns';

import { Language } from '@/translations';
import { capitalize } from '@/common/services/utils';

export function getDate(date: Date | string | number | null | undefined) {
    if (date === null || date === undefined) {
        return null;
    }

    let dateToFormat = date;
    if (!(dateToFormat instanceof Date)) {
        dateToFormat = new Date(date);
    }

    if (isNaN(dateToFormat.getTime())) {
        return null;
    }

    return dateToFormat;
}

export function formatDateString(
    date: Date | string | number | null | undefined,
    options?: { emptySymbol?: string; locale?: string }
) {
    const dateToFormat = getDate(date);

    if (dateToFormat) {
        return formatDate(dateToFormat, options?.locale);
    } else {
        return options?.emptySymbol ?? '';
    }
}

export function extractHoursFromDate(
    date: Date | string | number | null | undefined,
    options?: { emptySymbol?: string; locale?: string }
) {
    const hoursToFormat = getDate(date);
    if (hoursToFormat) {
        return hoursToFormat.toLocaleTimeString(options?.locale, { hour: '2-digit', minute: '2-digit' });
    } else {
        return options?.emptySymbol ?? '';
    }
}

export function formatFullDate(
    date: Date | string | number | null | undefined,
    options?: { emptySymbol?: string; locale?: string }
) {
    const dateToFormat = getDate(date);

    if (dateToFormat) {
        return dateToFormat.toLocaleDateString(options?.locale, {
            day: '2-digit',
            weekday: 'short',
            month: 'short',
            year: 'numeric',
        });
    } else {
        return options?.emptySymbol ?? '';
    }
}

export function formatMonthString(
    date: Date | string | number | null | undefined,
    options?: { emptySymbol?: string; locale?: string }
) {
    const dateToFormat = getDate(date);

    if (dateToFormat) {
        return formatMonth(dateToFormat, options?.locale);
    } else {
        return options?.emptySymbol ?? '';
    }
}

/**
 * Returns date in DD-MM-YY format with provided separators.
 * @param date - Date to be formatted
 * @param locale - Locale to format
 */
export function formatDate(date: Date, locale: string = navigator.language): string {
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Returns date in MMMM YYYY (i.e. June 2021) format with provided separators.
 * @param date - Date to be formatted
 * @param locale - Locale to format
 */
export function formatMonth(date: Date, locale: string = navigator.language): string {
    return capitalize(
        date.toLocaleString(locale, {
            month: 'long',
            year: 'numeric',
        })
    );
}

export function currentLanguageToDateFNSLocale(currentLanguage: Language): Locale {
    return Locales[currentLanguage.replace('-', '')] ?? Locales[currentLanguage.substring(0, 2)] ?? Locales.enUS;
}

const dayMilliseconds = 24 * 60 * 60 * 1000;

export function getDayStart(date?: Date | string | number | null | undefined) {
    date = getDate(date) || new Date();
    const tzOffset = date.getTimezoneOffset() * 60 * 1000;
    const ts = date.getTime();
    return new Date(ts - ((ts - tzOffset) % dayMilliseconds));
}

export function getDayEnd(date?: Date | string | number | null | undefined) {
    date = getDate(date) || new Date();
    const tzOffset = date.getTimezoneOffset() * 60 * 1000;
    const ts = date.getTime();
    return new Date(ts - ((ts - tzOffset) % dayMilliseconds) + dayMilliseconds - 1);
}

export function isMultipleDays(firstDate: Date, lastDate: Date) {
    return (
        firstDate.getFullYear() !== lastDate.getFullYear() ||
        firstDate.getMonth() !== lastDate.getMonth() ||
        firstDate.getDate() !== lastDate.getDate()
    );
}

export function dateToString(value: Date, currentLanguage: Language) {
    const date = Intl.DateTimeFormat(currentLanguage, {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
        weekday: 'long',
    }).format(value);
    const time = Intl.DateTimeFormat(currentLanguage, {
        hour: 'numeric',
        minute: 'numeric',
    }).format(value);

    return `${date} - ${time}`;
}
